import { FormEvent, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import { ErrorBlockText, TextInput } from '@/components/widget-components'
import { PaytrieAPI } from '@paytrie/client'
import { useSignupStore } from '@/reducers'
import {
  trackOnboardingFieldValidationFailed,
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'

interface ValidationErrors {
  sourceOfFunds?: string
  intendedUseOfUSDC?: string
  walletProvider?: string
  walletAddress?: string
}

export default function SignupAdditionalInformation1() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const additionalInfo = useSignupStore.use.additionalInfo()
  const setSourceOfFunds = useSignupStore.use.setSourceOfFunds()
  const setIntendedUseOfUSDC = useSignupStore.use.setIntendedUseOfUSDC()
  const setWalletProvider = useSignupStore.use.setWalletProvider()
  const setWalletAddress = useSignupStore.use.setWalletAddress()

  const goToNextState = useSignupStore.use.nextState()
  const [loading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [touched, setTouched] = useState<ValidationErrors>({})
  const [isFormValid, setIsFormValid] = useState(false)

  const validateForm = useCallback((): ValidationErrors => {
    const newErrors: ValidationErrors = {}

    if (!additionalInfo?.sourceOfFunds?.trim()) {
      newErrors.sourceOfFunds = t('additionalInfo.errors.sourceOfFundsRequired')
    }

    if (!additionalInfo?.intendedUseOfUSDC?.trim()) {
      newErrors.intendedUseOfUSDC = t(
        'additionalInfo.errors.intendedUseRequired'
      )
    }

    if (!additionalInfo?.walletProvider?.trim()) {
      newErrors.walletProvider = t(
        'additionalInfo.errors.walletProviderRequired'
      )
    }

    if (!additionalInfo?.walletAddress?.trim()) {
      newErrors.walletAddress = t('additionalInfo.errors.walletAddressRequired')
    }

    return newErrors
  }, [additionalInfo, t])

  const handleBlur = (field: keyof ValidationErrors) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const formErrors = validateForm()
    if (formErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: formErrors[field] }))
      trackOnboardingFieldValidationFailed(
        'funds_and_wallet',
        field,
        'required'
      )
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Check form validity whenever additionalInfo changes
  useEffect(() => {
    const formErrors = validateForm()
    const hasErrors = Object.keys(formErrors).length > 0
    const allFieldsFilled =
      additionalInfo?.sourceOfFunds?.trim() &&
      additionalInfo?.intendedUseOfUSDC?.trim() &&
      additionalInfo?.walletProvider?.trim() &&
      additionalInfo?.walletAddress?.trim()

    setIsFormValid(!hasErrors && !!allFieldsFilled)
  }, [additionalInfo, validateForm])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    trackOnboardingStepSubmitted('funds_and_wallet')
    setIsLoading(true)
    setErrorMessage('')

    try {
      const sourceOfFunds = additionalInfo?.sourceOfFunds?.trim()
      const intendedUseOfUSDC = additionalInfo?.intendedUseOfUSDC?.trim()
      const walletProvider = additionalInfo?.walletProvider?.trim()
      const walletAddress = additionalInfo?.walletAddress?.trim()

      if (
        !sourceOfFunds ||
        !intendedUseOfUSDC ||
        !walletProvider ||
        !walletAddress
      ) {
        setErrorMessage(t('additionalInfo.error.generic'))
        trackOnboardingStepFailed('funds_and_wallet', 'validation_error')
        return
      }

      const res = await PaytrieAPI.updateCurrentUser({
        olderUserInfo: {
          sourceOfFunds,
          intendedUseOfUSDC,
          walletProvider,
          walletAddress,
        },
        currentFlow:
          PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.ADDITIONAL_INFO,
      })

      if (res.status !== 200) {
        setErrorMessage(t('additionalInfo.error.generic'))
        trackOnboardingStepFailed('funds_and_wallet', 'unable_to_process')
        return
      }

      goToNextState()
      trackOnboardingStepCompleted('funds_and_wallet')
      navigate('/SignupAdditionalInformation2')
    } catch {
      setErrorMessage(t('additionalInfo.error.generic'))
      trackOnboardingStepFailed('funds_and_wallet', 'generic_error')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    trackOnboardingStepViewed('funds_and_wallet')
  }, [])

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={handleSubmit}
      data-testid="container-additional-information1-form"
    >
      <div className="text-lg font-normal uppercase">
        {t('additionalInfo.sourceOfFunds.question')}
      </div>
      <TextInput
        placeholder={t('additionalInfo.sourceOfFunds.placeholder')}
        value={additionalInfo?.sourceOfFunds || ''}
        onChange={(e) => setSourceOfFunds(e.target.value)}
        onBlur={() => handleBlur('sourceOfFunds')}
        data-testid="input-source-of-funds"
      />
      {touched.sourceOfFunds && errors.sourceOfFunds && (
        <ErrorBlockText data-testid="error-source-of-funds">
          {errors.sourceOfFunds}
        </ErrorBlockText>
      )}

      <div className="text-lg font-normal uppercase">
        {t('additionalInfo.intendedUse.question')}
      </div>
      <TextInput
        placeholder={t('additionalInfo.intendedUse.placeholder')}
        value={additionalInfo?.intendedUseOfUSDC || ''}
        onChange={(e) => setIntendedUseOfUSDC(e.target.value)}
        onBlur={() => handleBlur('intendedUseOfUSDC')}
        data-testid="input-intended-use"
      />
      {touched.intendedUseOfUSDC && errors.intendedUseOfUSDC && (
        <ErrorBlockText data-testid="error-intended-use">
          {errors.intendedUseOfUSDC}
        </ErrorBlockText>
      )}

      <div className="text-lg font-normal uppercase">
        {t('additionalInfo.walletProvider.question')}
      </div>
      <TextInput
        placeholder={t('additionalInfo.walletProvider.placeholder')}
        value={additionalInfo?.walletProvider || ''}
        onChange={(e) => setWalletProvider(e.target.value)}
        onBlur={() => handleBlur('walletProvider')}
        data-testid="input-wallet-provider"
      />
      {touched.walletProvider && errors.walletProvider && (
        <ErrorBlockText data-testid="error-wallet-provider">
          {errors.walletProvider}
        </ErrorBlockText>
      )}

      <div className="text-lg font-normal uppercase">
        {t('additionalInfo.walletAddress.question')}
      </div>
      <TextInput
        placeholder={t('additionalInfo.walletAddress.placeholder')}
        value={additionalInfo?.walletAddress || ''}
        onChange={(e) => setWalletAddress(e.target.value)}
        onBlur={() => handleBlur('walletAddress')}
        data-testid="input-wallet-address"
      />
      {touched.walletAddress && errors.walletAddress && (
        <ErrorBlockText data-testid="error-wallet-address">
          {errors.walletAddress}
        </ErrorBlockText>
      )}

      <ErrorBlockText data-testid="error-form-message">
        {errorMessage}
      </ErrorBlockText>

      <Button
        className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
        type="submit"
        disabled={loading || !isFormValid}
        data-testid="btn-continue"
      >
        {loading ? <LoadingIcon /> : t('additionalInfo.continue')}
      </Button>
    </form>
  )
}
